import { useState } from "react";
import { toast } from "sonner";
import { Printer } from "lucide-react";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/api";
import IDCard from "@/components/IDCard";
import { fileToDataUrl } from "@/utils/photo";

const inputCls = "w-full px-4 py-3 rounded-xl border border-emerald-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-700 text-sm";

export default function MemberDashboard() {
  const { t } = useLang();
  const { user, setUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", district: user?.district || "",
    taluka: user?.taluka || "", city: user?.city || "", village: user?.village || "",
    address: user?.address || "", designation: user?.designation || "", photo: user?.photo || null,
  });

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setForm((f) => ({ ...f, photo: dataUrl }));
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (!user) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/members/me", form);
      setUser(data);
      setEditing(false);
      toast.success(t.profile_saved);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14" data-testid="member-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-emerald-950" data-testid="dashboard-welcome">
            {t.welcome}, {user.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">{user.member_id}</p>
        </div>
        <span className={`self-start px-4 py-1.5 rounded-full text-xs font-bold ${
          user.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"
        }`} data-testid="member-status-badge">
          {user.status === "approved" ? t.approved : t.pending}
        </span>
      </div>

      <div className="grid lg:grid-cols-2 gap-10">
        <div className="flex flex-col items-center gap-5">
          <h2 className="font-heading text-xl font-bold text-emerald-950">{t.idcard_title}</h2>
          <IDCard member={user} />
          <button onClick={() => window.print()} data-testid="dashboard-print-card-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-900 text-white font-semibold hover:bg-emerald-800 transition-colors">
            <Printer className="w-4 h-4" /> {t.print_card}
          </button>
        </div>

        <div className="bg-white rounded-2xl border border-emerald-100 shadow-sm p-8 h-fit">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-xl font-bold text-emerald-950">{t.profile}</h2>
            <button onClick={() => setEditing(!editing)} data-testid="edit-profile-toggle-btn"
              className="px-4 py-2 rounded-full border border-emerald-700 text-emerald-800 text-sm font-semibold hover:bg-emerald-50 transition-colors">
              {t.edit_profile}
            </button>
          </div>

          {editing ? (
            <form onSubmit={handleSave} className="space-y-4" data-testid="profile-edit-form">
              <div>
                <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{t.form_photo}</label>
                <input type="file" accept="image/*" onChange={handlePhoto} className={inputCls} data-testid="profile-photo-input" />
                {form.photo && <img src={form.photo} alt="preview" className="mt-2 w-20 h-24 object-cover rounded-lg border border-emerald-200" data-testid="profile-photo-preview" />}
              </div>
              {[
                ["name", t.form_name], ["email", t.form_email], ["district", t.form_district],
                ["taluka", t.form_taluka], ["city", t.form_city], ["village", t.form_village],
                ["address", t.form_address], ["designation", t.form_designation],
              ].map(([k, label]) => (
                <div key={k}>
                  <label className="block text-xs font-semibold text-emerald-900 mb-1.5 uppercase tracking-wide">{label}</label>
                  <input value={form[k]} onChange={set(k)} className={inputCls} data-testid={`profile-${k}-input`} />
                </div>
              ))}
              <button type="submit" disabled={saving} data-testid="profile-save-btn"
                className="w-full py-3 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-bold transition-all disabled:opacity-60">
                {saving ? "..." : t.save}
              </button>
            </form>
          ) : (
            <dl className="space-y-3 text-sm" data-testid="profile-view">
              {[
                [t.form_name, user.name], [t.form_mobile, user.mobile], [t.form_email, user.email || "—"],
                [t.level, t[`level_${user.level}`] || user.level], [t.district, user.district],
                [t.form_taluka, user.taluka || "—"], [t.form_city, user.city || "—"],
                [t.form_village, user.village || "—"], [t.designation, user.designation || "Member"],
              ].map(([label, value], i) => (
                <div key={i} className="flex justify-between border-b border-emerald-50 pb-2">
                  <dt className="text-slate-500">{label}</dt>
                  <dd className="font-semibold text-emerald-950 text-right">{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
}
